import { useState, useEffect, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Capacitor } from "@capacitor/core";
import { useAuth } from "@/hooks/useAuth";
import { getHealthConnectedState } from "@/lib/appleHealth";
import { useLastHealthSyncTime } from "@/hooks/useLastHealthSyncTime";
import { supabase } from "@/integrations/supabase/client";
import {
  Footprints,
  Route,
  Flame,
  Heart,
  Activity,
  Moon,
  Plus,
  X,
  Watch,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import { cn } from "@/lib/utils";
import {
  formatHealthNumberSmart,
  formatHealthPercentScore,
  formatSleepDuration,
} from "@/lib/formatHealthValue";
import { getLocalDayKey, getLocalDateDaysAgo } from "@/lib/localDateRange";
import { useUnitFormatters } from "@/hooks/useUnits";
import { distanceUnitShort, kmToDisplayDistance } from "@/lib/units";

const DASHBOARD_CARDS_KEY = "straiv_dashboard_health_cards";

export type HealthCardMetric =
  | "steps"
  | "distance_km"
  | "active_energy_kcal"
  | "exercise_minutes"
  | "stand_hours"
  | "resting_heart_rate_bpm"
  | "vo2_max_ml_per_kg_min"
  | "sleep_hours"
  | "flights_climbed"
  | "stress_score"
  | "strain";

const METRIC_CONFIG: Record<
  HealthCardMetric,
  { icon: React.ElementType; unit: string; dbColumn: string }
> = {
  steps: { icon: Footprints, unit: "steps", dbColumn: "steps" },
  distance_km: {
    icon: Route,
    unit: "km",
    dbColumn: "distance_m",
  },
  active_energy_kcal: {
    icon: Flame,
    unit: "kcal",
    dbColumn: "active_energy_kcal",
  },
  exercise_minutes: {
    icon: Activity,
    unit: "min",
    dbColumn: "exercise_minutes",
  },
  stand_hours: {
    icon: Activity,
    unit: "h",
    dbColumn: "stand_hours",
  },
  resting_heart_rate_bpm: {
    icon: Heart,
    unit: "bpm",
    dbColumn: "resting_heart_rate_bpm",
  },
  vo2_max_ml_per_kg_min: {
    icon: Activity,
    unit: "ml/kg·min",
    dbColumn: "vo2_max_ml_per_kg_min",
  },
  sleep_hours: {
    icon: Moon,
    unit: "h",
    dbColumn: "sleep_hours",
  },
  flights_climbed: {
    icon: Activity,
    unit: "flights",
    dbColumn: "flights_climbed",
  },
  stress_score: {
    icon: Heart,
    unit: "",
    dbColumn: "stress_score",
  },
  strain: {
    icon: Activity,
    unit: "",
    dbColumn: "strain",
  },
};

const METRIC_LABEL_KEYS: Record<HealthCardMetric, string> = {
  steps: "healthData.metrics.steps.label",
  distance_km: "healthData.metrics.distance.label",
  active_energy_kcal: "healthData.metrics.activeCalories.label",
  exercise_minutes: "healthData.metrics.exerciseMinutes.label",
  stand_hours: "healthData.metrics.standHours.label",
  resting_heart_rate_bpm: "healthData.metrics.restingHeartRate.label",
  vo2_max_ml_per_kg_min: "healthData.metrics.vo2Max.label",
  sleep_hours: "healthData.metrics.sleep.label",
  flights_climbed: "healthData.metrics.flightsClimbed.label",
  stress_score: "healthData.metrics.stressScore.label",
  strain: "healthData.metrics.strain.label",
};

function getStoredCards(userId: string | undefined): HealthCardMetric[] {
  if (!userId) return [];
  try {
    const raw = localStorage.getItem(`${DASHBOARD_CARDS_KEY}_${userId}`);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as string[];
    return parsed.filter((k): k is HealthCardMetric =>
      Object.keys(METRIC_CONFIG).includes(k)
    );
  } catch {
    return [];
  }
}

function setStoredCards(userId: string, cards: HealthCardMetric[]) {
  localStorage.setItem(`${DASHBOARD_CARDS_KEY}_${userId}`, JSON.stringify(cards));
}

async function fetchLatestValue(
  userId: string,
  metric: HealthCardMetric
): Promise<number | null> {
  const config = METRIC_CONFIG[metric];
  const today = getLocalDayKey();

  // Sleep: wake-day attribution — only show last night's sleep (today's row or
  // manual log for today). No fallback to older nights.
  if (metric === "sleep_hours") {
    const today = getLocalDayKey();

    const healthRes = await supabase
      .from("health_metrics")
      .select("sleep_hours")
      .eq("user_id", userId)
      .eq("metric_date", today)
      .maybeSingle();
    const healthH = (healthRes.data as { sleep_hours?: number } | null)?.sleep_hours;
    if (healthH != null && healthH > 0 && Number.isFinite(healthH)) return healthH;

    const manualRes = await supabase
      .from("sleep_logs")
      .select("duration_minutes, hours_slept")
      .eq("user_id", userId)
      .eq("sleep_date", today)
      .maybeSingle();
    const manual = manualRes.data as
      | { duration_minutes?: number | null; hours_slept?: number | null }
      | null;
    if (manual) {
      const fromMinutes =
        manual.duration_minutes != null && manual.duration_minutes > 0
          ? manual.duration_minutes / 60
          : null;
      const val = fromMinutes ?? (manual.hours_slept != null && manual.hours_slept > 0 ? manual.hours_slept : null);
      if (val != null && Number.isFinite(val)) return val;
    }
    return null;
  }

  const startDate = getLocalDayKey(getLocalDateDaysAgo(30));

  // Fix #1 (data sync): for non-sleep metrics like steps/calories/exercise,
  // prefer TODAY's row even if it's null/0 — that's the user's "real-time" value.
  // Falls back to most recent non-null only when today has no row at all.
  const todayRes = await supabase
    .from("health_metrics")
    .select(`${config.dbColumn}, metric_date`)
    .eq("user_id", userId)
    .eq("metric_date", today)
    .maybeSingle();
  const todayVal = todayRes.data
    ? (todayRes.data as unknown as Record<string, unknown>)[config.dbColumn]
    : null;
  if (todayVal != null) {
    let num = Number(todayVal);
    if (metric === "distance_km" && config.dbColumn === "distance_m") num = num / 1000;
    if (Number.isFinite(num)) {
      if (metric === "strain" || metric === "stress_score") {
        num = Math.max(0, Math.min(100, num));
      }
      return num;
    }
  }

  // Today's row exists but this cumulative metric is still null — show 0 instead
  // of yesterday's value so steps/calories don't look "stuck" before sync lands.
  const cumulativeTodayMetrics: HealthCardMetric[] = [
    "steps",
    "distance_km",
    "active_energy_kcal",
    "exercise_minutes",
    "flights_climbed",
    "stand_hours",
  ];
  if (todayRes.data && cumulativeTodayMetrics.includes(metric)) {
    return metric === "distance_km" ? 0 : 0;
  }

  // Stress/strain and other sparse metrics: fall back to most recent non-null.
  const { data, error } = await supabase
    .from("health_metrics")
    .select(config.dbColumn)
    .eq("user_id", userId)
    .gte("metric_date", startDate)
    .not(config.dbColumn, "is", null)
    .order("metric_date", { ascending: false })
    .limit(1)
    .maybeSingle();

  if (error || !data) return null;
  const val = (data as unknown as Record<string, unknown>)[config.dbColumn];
  if (val == null) return null;

  let num = Number(val);
  if (metric === "distance_km" && config.dbColumn === "distance_m") {
    num = num / 1000;
  }
  if (!Number.isFinite(num)) return null;
  if (metric === "strain" || metric === "stress_score") {
    num = Math.max(0, Math.min(100, num));
  }
  return num;
}

function formatValue(metric: HealthCardMetric, value: number, units?: "metric" | "imperial"): string {
  if (metric === "distance_km") {
    const display = units ? kmToDisplayDistance(value, units) : value;
    return display.toFixed(2);
  }
  if (metric === "strain") {
    return formatHealthPercentScore(value);
  }
  if (metric === "stress_score") {
    return formatHealthNumberSmart(value);
  }
  if (
    metric === "sleep_hours"
  ) {
    return formatSleepDuration(value);
  }
  if (
    metric === "vo2_max_ml_per_kg_min" ||
    metric === "stand_hours"
  ) {
    return formatHealthNumberSmart(value);
  }
  return Math.round(value).toString();
}

export const AppleHealthCardsSection = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { units } = useUnitFormatters();
  const [connected, setConnected] = useState<boolean | null>(null);
  const [cards, setCards] = useState<HealthCardMetric[]>([]);
  const [showAddSheet, setShowAddSheet] = useState(false);
  const [values, setValues] = useState<Record<HealthCardMetric, number | null>>(
    {} as Record<HealthCardMetric, number | null>
  );

  const isIOS = Capacitor.isNativePlatform() && Capacitor.getPlatform() === "ios";
  const { formatted: lastSyncFormatted } = useLastHealthSyncTime(user?.id ?? null);

  const metricLabels = useMemo(() => {
    return Object.fromEntries(
      (Object.keys(METRIC_LABEL_KEYS) as HealthCardMetric[]).map((key) => [
        key,
        t(METRIC_LABEL_KEYS[key]),
      ])
    ) as Record<HealthCardMetric, string>;
  }, [t]);

  useEffect(() => {
    if (!isIOS) {
      setConnected(false);
      return;
    }
    if (user?.id) getHealthConnectedState(user.id).then(setConnected);
  }, [isIOS, user?.id]);

  useEffect(() => {
    if (user) {
      setCards(getStoredCards(user.id));
    }
  }, [user?.id]);

  const loadValues = useCallback(async () => {
    if (!user?.id || cards.length === 0) {
      setValues({} as Record<HealthCardMetric, number | null>);
      return;
    }
    const pairs = await Promise.all(
      cards.map(async (m) => [m, await fetchLatestValue(user.id, m)] as const)
    );
    const next = Object.fromEntries(pairs) as Record<
      HealthCardMetric,
      number | null
    >;
    setValues(next);
  }, [user?.id, cards]);

  useEffect(() => {
    loadValues();
  }, [loadValues]);

  // Refetch when HealthKit sync completes (app foreground / Health tab)
  useEffect(() => {
    const onSyncComplete = () => loadValues();
    window.addEventListener('health-sync-complete', onSyncComplete);
    return () => window.removeEventListener('health-sync-complete', onSyncComplete);
  }, [loadValues]);

  // Native WebView often focuses the first grid item after sheet opens → looks "pre-selected"
  useEffect(() => {
    if (!showAddSheet || !Capacitor.isNativePlatform()) return;
    const id = window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        const el = document.activeElement;
        if (el instanceof HTMLElement && el !== document.body) el.blur();
      });
    });
    return () => cancelAnimationFrame(id);
  }, [showAddSheet]);

  const addCard = (metric: HealthCardMetric) => {
    if (!user || cards.includes(metric)) return;
    const next = [...cards, metric];
    setCards(next);
    setStoredCards(user.id, next);
    setShowAddSheet(false);
    void fetchLatestValue(user.id, metric).then((v) => {
      setValues((prev) => ({ ...prev, [metric]: v }));
    });
  };

  const removeCard = (metric: HealthCardMetric) => {
    if (!user) return;
    const next = cards.filter((c) => c !== metric);
    setCards(next);
    setStoredCards(user.id, next);
  };

  const availableToAdd = (
    Object.keys(METRIC_CONFIG) as HealthCardMetric[]
  ).filter((k) => !cards.includes(k));

  if (!isIOS || connected !== true) return null;

  return (
    <div className="w-full max-w-md flex-shrink-0 mt-2">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">
            {t('components.appleHealthCards.sectionTitle')}
          </h3>
          {lastSyncFormatted && (
            <span className="text-xs text-muted-foreground">
              · {t('components.appleHealthCards.syncedPrefix', { time: lastSyncFormatted })}
            </span>
          )}
        </div>
        <Button
          variant="ghost"
          size="sm"
          className="gap-1.5 text-primary hover:text-primary/90"
          onClick={() => setShowAddSheet(true)}
          title={t('components.appleHealthCards.addButtonTitle')}
        >
          <Plus className="h-4 w-4" />
          <span className="text-sm">{t('components.appleHealthCards.addCard')}</span>
        </Button>
      </div>

      <div className="grid grid-cols-2 gap-2">
        {cards.map((metric) => {
          const config = METRIC_CONFIG[metric];
          const Icon = config.icon;
          const value = values[metric];
          return (
            <div
              key={metric}
              className={cn(
                "relative flex items-center gap-3 rounded-xl px-4 py-3 w-full min-w-0",
                "bg-gradient-card border border-white/10",
                "backdrop-blur-sm outline-none ring-0 shadow-sm",
                "[-webkit-tap-highlight-color:transparent]"
              )}
            >
              <button
                onClick={() => removeCard(metric)}
                className="absolute top-1.5 right-1.5 p-1 rounded-md hover:bg-white/10 text-muted-foreground"
                aria-label={t('components.appleHealthCards.removeCardAria')}
              >
                <X className="h-3.5 w-3.5" />
              </button>
              <div className="flex-shrink-0 w-9 h-9 rounded-lg bg-primary/20 flex items-center justify-center">
                <Icon className="h-4 w-4 text-primary" />
              </div>
              <div className="min-w-0 pr-6">
                <p className="text-xs text-muted-foreground truncate">
                  {metricLabels[metric]}
                </p>
                <p className="text-lg font-bold text-foreground">
                  {value != null
                    ? formatValue(metric, value, units)
                    : t('components.appleHealthCards.noValue')}
                  {metric !== "sleep_hours" && (
                    <span className="text-xs font-normal text-muted-foreground ml-0.5">
                      {metric === "distance_km" ? distanceUnitShort(units) : config.unit}
                    </span>
                  )}
                </p>
              </div>
            </div>
          );
        })}
      </div>

      {cards.length === 0 && (
        <div
          className={cn(
            "flex flex-col items-center justify-center gap-2 rounded-xl border border-dashed border-white/20 py-6 px-4",
            "bg-white/5"
          )}
        >
          <Watch className="h-8 w-8 text-primary/60" />
          <p className="text-sm text-muted-foreground text-center">
            {t('components.appleHealthCards.emptyHint')}
          </p>
          <Button
            variant="outline"
            size="sm"
            className="gap-2 border-primary/30"
            onClick={() => setShowAddSheet(true)}
          >
            <Plus className="h-4 w-4" />
            {t('components.appleHealthCards.addFirstCard')}
          </Button>
        </div>
      )}

      <Sheet open={showAddSheet} onOpenChange={setShowAddSheet}>
        <SheetContent
          side="bottom"
          className="rounded-t-2xl max-h-[85vh] overflow-y-auto"
          onOpenAutoFocus={(e) => e.preventDefault()}
          onCloseAutoFocus={(e) => e.preventDefault()}
        >
          <SheetHeader>
            <SheetTitle>{t('components.appleHealthCards.sheetTitle')}</SheetTitle>
            <SheetDescription>
              {t('components.appleHealthCards.sheetDescription')}
            </SheetDescription>
          </SheetHeader>
          <div className="grid grid-cols-2 gap-2 mt-6 pb-8">
            {availableToAdd.map((metric) => {
              const config = METRIC_CONFIG[metric];
              const Icon = config.icon;
              return (
                <button
                  type="button"
                  key={metric}
                  onClick={() => addCard(metric)}
                  className={cn(
                    "flex items-center gap-3 rounded-xl p-4 text-left outline-none ring-0 ring-offset-0",
                    "focus:outline-none focus-visible:outline-none focus-visible:ring-0",
                    "bg-muted/50 hover:bg-muted border border-transparent hover:border-white/20",
                    "transition-colors active:scale-[0.99] [-webkit-tap-highlight-color:transparent]"
                  )}
                >
                  <div className="w-10 h-10 rounded-lg bg-primary/20 flex items-center justify-center flex-shrink-0">
                    <Icon className="h-5 w-5 text-primary" />
                  </div>
                  <div className="min-w-0">
                    <p className="font-medium text-foreground">{metricLabels[metric]}</p>
                    <p className="text-xs text-muted-foreground">
                      {metric === "distance_km" ? distanceUnitShort(units) : config.unit}
                    </p>
                  </div>
                </button>
              );
            })}
          </div>
          {availableToAdd.length === 0 && (
            <p className="text-center text-muted-foreground py-8">
              {t('components.appleHealthCards.allAdded')}
            </p>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
};
